// all services
export const services = [
  {
    title: "AI Employees",
    image: require("../assets/images/ai-telecalling.png"),
    shortDesc:
      "Deploy autonomous AI digital employees—AI SDRs, Support Specialists, Ops Managers, and Data Analysts—operating 24/7 at sub-200ms latency, zero churn, and 85% cost reduction.",
    technologies: ["Autonomous Agents", "LLMs", "LangChain", "WebRTC", "Python", "Vector DBs", "Redis"],
    detailsPageImages: {
      first: require("../assets/images/services/aicalling1.webp"),
      second: require("../assets/images/services/aiautomation2.webp"),
      third: require("../assets/images/services/aicalling3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Architecting the #1 Autonomous Digital Workforce for Modern Enterprises
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    The modern corporate bottleneck is no longer technology—it is human operational bandwidth, hiring delays, training churn, and fragmented communication. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we are recognized as the <span class="font-semibold text-primary">#1 pioneer in Autonomous AI Employees & Digital Workers</span>. We architect, train, and deploy cognitive digital team members that execute complex business functions with precision, empathy, and speed.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Unlike brittle rule-based chatbots or disconnected SaaS automations, a PANTHM AI Employee operates with contextual reasoning, long-term vector memory, and direct database execution capabilities. Powered by our proprietary <span class="font-semibold text-primary">PANTHM Consolidation Architecture (PCA)</span>, our AI workers operate with zero sync lag and sub-200ms response times, integrating directly with your CRMs, ERPs, communication channels, and internal APIs.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    From autonomous Sales SDRs that qualify inbound buyers in real time to 24/7 Customer Support Officers and Hotel Concierges synced directly with PMS systems, PANTHM delivers an enterprise-grade digital workforce that scales infinitely without fatigue, sick days, or payroll inflation.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Core AI Employee Roles We Deploy for Global Enterprises:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Autonomous AI Sales Development Representative (SDR):</span> 
      Proactively qualifies inbound website visitors, cold leads, and database contacts via ultra-low latency voice calls and WhatsApp. Books qualified meetings directly onto your sales team's calendar with 100% adherence to your ideal customer profile (ICP).
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">24/7 AI Customer Support & Triage Specialist:</span> 
      Delivers instant, zero-wait-time customer support across voice, live chat, and email. Resolves technical inquiries, handles billing issues, and executes account actions by querying your internal knowledge base with deterministic accuracy.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">AI Operations & Data Synchronization Officer:</span> 
      Replaces error-prone manual data entry between legacy software, CRMs, ERPs, and spreadsheets. Executes background worker tasks, reconciles accounts, and automates multi-step approval workflows.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Autonomous Hospitality & Club Concierge:</span> 
      Equipped with native bi-directional PMS integrations (<span class="font-semibold text-primary">Cloudbeds, Mews, StayNTouch, Oracle Opera, Hotelogix</span>) to handle real-time room availability, guest bookings, membership renewals, and 24/7 guest requests with zero human delay.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">AI Business Intelligence & Executive Reporting Analyst:</span> 
      Continuously monitors your company's KPIs, marketing performance, and financial metrics. Generates daily executive briefing memos, detects operational anomalies, and forecasts revenue trends.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Why PANTHM AI Labs is Ranked #1 in Enterprise AI Employees
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Traditional agencies configure off-the-shelf wrappers that break when APIs change. <span class="font-semibold text-primary">PANTHM AI Labs builds sovereign digital workers from first principles</span>. We maintain 100% code ownership, self-hosted LLM orchestration pipelines, and strict enterprise security controls.
  </p>
  <ul class="list-disc pl-6 mb-6 text-slate-700 dark:text-slate-300 space-y-3">
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">Sub-200ms Conversational Latency:</span>
      Industry-leading voice responsiveness that feels completely human and natural.
    </li>
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">85% Labor Cost Reduction:</span>
      Dramatically cut customer support and sales outreach expenditures while scaling capacity by 100x.
    </li>
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">Zero Middleware (PCA Architecture):</span>
      Direct database triggers eliminate fragile Zapier/Make connections and recurring API license costs.
    </li>
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">Enterprise Security & Compliance:</span>
      SOC2 Type II-ready, GDPR, DPDP compliance, with private on-premise or sovereign cloud hosting options.
    </li>
  </ul>
  `,
    },
  },
  {
    title: "AI Calling Agency",
    image: require("../assets/images/ai-telecalling.png"),
    shortDesc:
      "Full-service enterprise AI Calling Agency custom-engineering human-like voice AI SDRs, inbound receptionists, and automated calling pipelines operating at sub-200ms conversational latency.",
    technologies: ["WebRTC Streaming", "Deepgram Nova-2", "Cartesia Sonic", "Ollama / Llama-3.3", "FastAPI", "Twilio SIP", "Redis Streams"],
    detailsPageImages: {
      first: require("../assets/images/services/aicalling1.webp"),
      second: require("../assets/images/services/aicalling2.webp"),
      third: require("../assets/images/services/aicalling3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">
    Enterprise AI Calling Agency: Sub-200ms Conversational Voice Pipelines
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base font-normal">
    In enterprise telecalling and voice automation, conversational latency is the single most critical factor determining user retention and close rates. When response latency exceeds 500 milliseconds, natural conversational pacing breaks down, causing unnatural interruptions and high caller hang-up rates. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, our engineering studio builds sovereign voice AI agents operating at verified sub-200ms conversational turn-around. Rather than relying on generic multi-tenant SaaS wrappers that route audio through third-party middleware, we engineer custom full-duplex WebRTC streaming pipelines. Our stack combines Deepgram Nova-2 for ultra-fast speech recognition (70ms), streaming inference with Llama-3.3 and GPT-4o models (60ms), and Cartesia Sonic for near-instant neural voice synthesis (35ms). With zero-middleware database synchronization and direct SIP trunking, enterprise organizations achieve fluid, human-like voice communication that accelerates sales qualification, resolves inbound support inquiries, and reduces operational call center expenditures by up to 85%.
  </p>
  <p class="mb-8 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Traditional call center models suffer from mounting payroll overhead, agent burnout, multi-week training cycles, and erratic lead qualification. PANTHM AI Labs replaces fragile manual telecalling with deterministic digital voice agents that maintain 100% brand consistency, handle rejection with composure, and scale from 10 to 100,000 concurrent calls with zero infrastructure degradation. Test our low-latency voice pipeline live on our <a href="/tools/voice-ai-demo" class="text-primary font-semibold hover:underline">Interactive Voice AI Demo</a> or explore our multi-agent <a href="/services/ai-employees" class="text-primary font-semibold hover:underline">Autonomous AI Employees</a>.
  </p>
  `,
      secondSection: `<h2 class="text-2xl md:text-3xl font-bold mb-6 text-slate-900 dark:text-white">
    Enterprise Voice AI Calling Capabilities & Industry Deployment Matrix
  </h2>
  <div class="grid md:grid-cols-2 gap-6 mb-8 text-slate-700 dark:text-slate-300">
    <div class="p-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
      <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Autonomous Outbound Voice SDR
      </h3>
      <p class="text-sm leading-relaxed">
        Supercharges B2B pipeline velocity by dialing cold prospects, conducting ICP qualification, handling objections dynamically, and booking qualified appointments directly onto account executives' Google Calendar or Outlook schedules.
      </p>
    </div>
    <div class="p-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
      <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-primary"></span> 24/7 Inbound Voice Concierge & Receptionist
      </h3>
      <p class="text-sm leading-relaxed">
        Eliminates customer hold times completely. Handles complex routing, answers FAQs via vector knowledge retrieval, triages technical emergencies, and performs graceful live-agent handoffs with live audio transcript context.
      </p>
    </div>
    <div class="p-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
      <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-indigo-500"></span> Real Estate & PropTech Lead Acceleration
      </h3>
      <p class="text-sm leading-relaxed">
        Instantly calls inbound property inquiries within 15 seconds of portal submission. Qualifies homebuyer budgets, location preferences, and financing pre-approval, scheduling private on-site property viewings automatically.
      </p>
    </div>
    <div class="p-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
      <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-cyan-500"></span> Healthcare & Clinical Appointment Triage
      </h3>
      <p class="text-sm leading-relaxed">
        Executes automated appointment reminders, patient recall calls, and pre-procedure instructions. Drastically slashes clinical no-show rates while adhering to strict HIPAA and confidential health data governance standards.
      </p>
    </div>
  </div>

  <h3 class="text-xl font-bold mb-4 text-slate-900 dark:text-white">
    Native Hotel PMS & Enterprise CRM Bi-Directional Synchronization
  </h3>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Enterprise scalability requires voice agents to interact directly with internal operational software rather than operating in an isolated silo. PANTHM AI Labs engineers native, bi-directional API synchronizations between conversational voice AI pipelines and mission-critical enterprise systems. In the hospitality sector, our voice concierges integrate natively with leading Property Management Systems (PMS) and Central Reservation Systems (CRS)—including Oracle Opera PMS, Cloudbeds, Mews, StayNTouch, and Hotelogix. This enables AI callers to query real-time room inventories, execute confirmed bookings, process membership renewals, and handle guest concierge requests with zero latency. For B2B sales and real estate operations, our voice SDR agents synchronize bi-directionally with Salesforce, HubSpot, Zoho, and custom PostgreSQL databases. Every call generates an instant structured transcript, customer sentiment score, and CRM contact update. By writing directly to enterprise databases, PANTHM eliminates brittle third-party automation tools like Zapier, ensuring strict data governance and 99.99% system availability.
  </p>
  `,
      thirdSection: `<h2 class="text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">
    Custom Voice Engineering vs. Off-The-Shelf SaaS Wrappers
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Many organizations initially test off-the-shelf voice SaaS tools like Bland AI, Retell AI, or Vapi, only to encounter severe limitations in latency stability, custom tool calling, and proprietary data privacy. While DIY voice platforms charge per-minute markups on shared multi-tenant servers, enterprise deployments require dedicated cloud infrastructure with zero data retention. PANTHM AI Labs operates as a full-service custom engineering agency, building bespoke voice architectures that clients own outright. We deploy custom fine-tuned voice models, optimized speech streaming servers, and private SIP trunks hosted directly within your sovereign cloud VPC or on-premise data centers. This eliminates vendor platform lock-in, recurring per-seat licensing fees, and telemetry leakage to third-party ad networks. Furthermore, our enterprise pipelines include end-to-end TCPA, FCC, and carrier STIR/SHAKEN compliance protocols, protecting your brand reputation while dialing high-volume outbound campaigns. With PANTHM, you deploy an enterprise-grade calling infrastructure tailored to your exact business logic.
  </p>
  
  <div class="overflow-x-auto my-8">
    <table class="w-full text-left border-collapse text-sm text-slate-700 dark:text-slate-300">
      <thead>
        <tr class="border-b border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-bold bg-slate-100 dark:bg-white/5">
          <th class="py-3 px-4">Architecture Parameter</th>
          <th class="py-3 px-4 text-primary">PANTHM AI Labs (Custom Agency)</th>
          <th class="py-3 px-4 text-slate-500">Generic SaaS Wrappers</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-200 dark:divide-white/5">
        <tr>
          <td class="py-3 px-4 font-semibold">Conversational Latency</td>
          <td class="py-3 px-4 text-emerald-600 dark:text-emerald-400 font-bold">&lt; 200ms Verified (Full-Duplex WebRTC)</td>
          <td class="py-3 px-4 text-slate-400">800ms – 1,800ms (High lag & interruption issues)</td>
        </tr>
        <tr>
          <td class="py-3 px-4 font-semibold">Data Sovereignty & Privacy</td>
          <td class="py-3 px-4 text-emerald-600 dark:text-emerald-400 font-bold">Zero-Retention Private VPC / On-Premise</td>
          <td class="py-3 px-4 text-slate-400">Shared multi-tenant servers & third-party logs</td>
        </tr>
        <tr>
          <td class="py-3 px-4 font-semibold">PMS & CRM Integration</td>
          <td class="py-3 px-4 text-emerald-600 dark:text-emerald-400 font-bold">Native Bi-Directional Database Sockets</td>
          <td class="py-3 px-4 text-slate-400">Fragile Zapier/Make webhooks with sync delays</td>
        </tr>
        <tr>
          <td class="py-3 px-4 font-semibold">Code & Asset Ownership</td>
          <td class="py-3 px-4 text-emerald-600 dark:text-emerald-400 font-bold">100% Client Codebase & Voice Persona Ownership</td>
          <td class="py-3 px-4 text-slate-400">Locked inside vendor platform subscription</td>
        </tr>
        <tr>
          <td class="py-3 px-4 font-semibold">Regulatory Compliance</td>
          <td class="py-3 px-4 text-emerald-600 dark:text-emerald-400 font-bold">TCPA, FCC, A2P 10DLC & STIR/SHAKEN Built-In</td>
          <td class="py-3 px-4 text-slate-400">Basic API without spam carrier reputation shielding</td>
        </tr>
      </tbody>
    </table>
  </div>
  `,
    },
  },
  {
    title: "Web Development",
    image: require("../assets/images/web-dev-vector.png"),
    shortDesc:
      "Architecting high-performance digital ecosystems with custom frontend architectures and serverless backends.",
    technologies: ["React", "Angular", "Node", "Express", "MongoDB"],
    detailsPageImages: {
      first: require("../assets/images/services/webdevelopment1.jpg"),
      second: require("../assets/images/services/webdevelopment2.png"),
      third: require("../assets/images/services/webdevelopment3.png"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Forging Your Digital Identity with Next-Gen Custom Web Engineering
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    In the modern digital economy, your web application is more than just a public billboard—it is the hub of your business operations, the primary channel for user acquisition, and the core of your brand's digital identity. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we build custom web applications that combine stunning aesthetic designs with elite engineering performance. We specialize in developing single-page applications (SPAs) and server-side rendered (SSR) platforms optimized for maximum speed and conversion.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We do not believe in generic templates or bloated WordPress setups. WordPress sites and theme templates carry massive code overhead, leading to slow page loads, poor search rankings, and security vulnerabilities. Instead, our team writes clean, modular code from first principles using technologies like React, Vite, Node.js, and MongoDB Atlas. By eliminating unnecessary dependencies, we ensure your website achieves near-perfect Core Web Vitals (LCP, INP, CLS) and load speeds.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Our engineering team specializes in building scalable custom portals, interactive corporate dashboards, global SaaS platforms, and e-commerce architectures. Every application is designed to scale dynamically, handling surges in traffic without performance degradation. We integrate robust API orchestration layers to connect your website directly to your databases, billing gateways, and external automation tools, establishing a unified digital workspace.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Full-Stack Web Development Capabilities:
  </h2>
  <ul class="list-disc pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Bespoke Enterprise Web Applications:</span> 
      Custom-coded dashboards, web platforms, and back-office portals tailored to automate your company's proprietary operations and database synchronization.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">High-Performance E-Commerce Architectures:</span> 
      Immersive digital storefronts built on headless commerce frameworks, featuring dynamic search, fast checkout funnels, and predictive purchase algorithms.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Progressive Web Apps (PWAs):</span> 
      Websites that look, feel, and function like native mobile applications, providing offline capability, push notifications, and fast loading speeds.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Headless CMS Integrations:</span> 
      Empower your marketing team with flexible, modern content management systems while maintaining the performance and security benefits of a static frontend.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Secure API & Microservices Development:</span> 
      Build custom RESTful and GraphQL API pipelines to orchestrate data exchange between your web app, databases, and third-party software.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Optimized for Search Engines & LLM Crawlers (LLMO/GEO)
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Traditional web development ignores how modern search engines operate. Today, websites are not only indexed by Googlebot, but they are also parsed by AI search engines like ChatGPT, Perplexity, and SearchGPT. If your site's code is bloated, poorly structured, or heavily reliant on client-side JavaScript execution, AI crawlers will fail to read your content, causing you to lose valuable citation traffic.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    At PANTHM AI Labs, we build websites with search engines and LLMs in mind. We structure our HTML using strict semantic tags, embed schema markup (JSON-LD), and optimize sitemaps for fast discovery. By implementing server-side pre-rendering and caching policies, we allow Googlebot and AI crawlers to read your content instantly in clean, readable formats.
  </p>
  <ul class="list-disc pl-6 mb-6 text-slate-700 dark:text-slate-300 space-y-3">
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">Core Web Vitals Mastery:</span>
      We optimize images, code execution paths, and server response times to ensure sub-200ms Largest Contentful Paint (LCP).
    </li>
    <li class="mb-2">
      <span class="font-semibold text-slate-900 dark:text-white">Sovereign Compliance:</span>
      We configure localized edge database instances to comply fully with data residency laws in the EU, UAE, and Australia.
    </li>
  </ul>
  `,
    },
  },
  {
    title: "App Development",
    image: require("../assets/images/app-dev-vector.png"),
    shortDesc:
      "Engineering native and cross-platform mobile applications optimized for performance, security, and fluid user experiences.",
    technologies: ["Swift", "React Native", "Flutter", "Java", "Kotlin"],
    detailsPageImages: {
      first: require("../assets/images/services/appdevelopment1.jpg"),
      second: require("../assets/images/services/appdevelopment2.png"),
      third: require("../assets/images/services/appdevelopment3.png"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
        Mobile-First Innovation for a Connected Global Market
      </h2>
      <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
        The smartphone is the primary interface of the modern digital experience. A business's mobile application is a direct line of communication with its target market. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we build high-performance mobile applications that engage users, build brand loyalty, and drive business growth. We combine pixel-perfect UI/UX design with robust backend logic to deliver mobile applications that stand out in competitive app marketplaces.
      </p>
      <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
        Our mobile team possesses deep expertise in both native mobile engineering (Swift for iOS, Kotlin/Java for Android) and high-efficiency cross-platform frameworks (Flutter, React Native). Whether you need an app that leverages native device hardware like sensors, Bluetooth, and biometric authentication, or a cross-platform solution to launch quickly on both stores with a single codebase, we provide the architectural design and code logic to ensure success.
      </p>
      <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
        We prioritize application performance, data security, and battery efficiency during the development phase. By writing optimized, lightweight code and using local database caching, our mobile apps load instantly, handle network transitions gracefully, and protect user data with bank-grade encryption protocols.
      </p>
      `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Mobile Engineering Capabilities:
  </h2>
  <ul class="list-disc pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Native iOS Development (Swift):</span> 
      High-velocity native applications engineered specifically for the iOS ecosystem, utilizing Apple's latest frameworks (SwiftUI, Combine, CoreML) and hardware features.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Native Android Development (Kotlin/Java):</span> 
      Robust native apps built for the diverse Android device ecosystem, optimized for performance, memory management, and compatibility across Android OS versions.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Flutter Cross-Platform Development:</span> 
      Unified codebase applications built on Google's Dart-based UI engine, delivering native-quality visual fidelity, animations, and performance on both iOS and Android.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">React Native Development:</span> 
      JavaScript-driven cross-platform applications that bridge directly to native UI components, enabling fast updates and code reusability across web and mobile.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Secure Local Sync & Offline Cache:</span> 
      Implement robust local databases (Realm, SQLite) and synchronization engines, allowing apps to function offline and sync data seamlessly when connectivity returns.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Security & App Store Optimization (ASO)
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    A great mobile application must be highly secure and discoverable. We build our apps using strict security protocols, enforcing SSL pinning, token-based API authentication, secure keychains for biometric logins, and code obfuscation to prevent reverse engineering.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We handle the entire App Store and Google Play submission process, from configuring developer consoles to App Store Optimization (ASO). We structure app descriptions, keywords, screenshots, and privacy policies to maximize organic downloads and ensure a smooth store review phase.
  </p>
  `,
    },
  },
  {
    title: "Game Development",
    image: require("../assets/images/game-dev-vector.png"),
    shortDesc:
      "Designing and developing immersive 2D, 3D, and XR gaming experiences using Unity and Unreal Engine.",
    technologies: ["Unity", "Unreal Engine", "C++"],
    detailsPageImages: {
      first: require("../assets/images/services/gamedevelopment1.jpg"),
      second: require("../assets/images/services/gamedevelopment2.png"),
      third: require("../assets/images/services/gamedevelopment3.png"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Forging New Realities through Custom Interactive Entertainment
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Game development represents the ultimate convergence of creative storytelling, interactive design, and low-level software engineering. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we are world-builders. We design and build immersive interactive experiences across mobile, desktop, console, and web platforms. Our studio blends game design principles with technical performance optimization to deliver games that captivate players.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We employ industry-leading game engines, Unity and Unreal Engine, along with custom C++ and C# coding logic. Whether you are aiming to launch a fast-paced multiplayer title, an immersive 3D simulation, or an educational gaming platform, we engineer the physics, rendering, state-synchronization, and audio pipelines to ensure fluid, high-frame-rate gameplay.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We optimize asset management, compile times, and memory allocation to ensure games run smoothly on all target hardware—from mobile phones with limited resources to powerful console and desktop systems. Our studio has a dedicated pipeline for 3D modeling, texturing, animation, and sound design, providing a cohesive creative direction for your project.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Custom Game Studio Capabilities:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Cross-Platform Game Development:</span> 
      Engineer and deploy interactive games to mobile (iOS/Android), desktop (Windows/macOS), consoles (PlayStation/Xbox/Switch), and WebGL/WebXR environments.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Multiplayer & Networking Architectures:</span> 
      Implement low-latency multiplayer logic, dedicated game server hosting configurations, WebSockets, and secure state-sync protocols to prevent cheating.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">AR/VR/XR Immersive Worlds:</span> 
      Pioneer interactive virtual reality (VR) and augmented reality (AR) environments using Unity XR and Unreal OpenXR for spatial training and entertainment.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Physics and Graphics Optimization:</span> 
      Fine-tune shaders, asset compression, level-of-detail (LOD) systems, and memory usage to maintain steady 60+ FPS performance on all viewports.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Player Engagement & Technical Scalability
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Great games require a balance of engaging core loops, satisfying mechanics, and reliable technical backend stability. We design player progression systems, in-game economies, and analytics dashboards that track player behavior, helping you refine gameplay loops post-launch.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We build game database backends using scalable serverless architectures, securing transaction endpoints, player registers, and leaderboards against external intrusion.
  </p>
  `,
    },
  },
  {
    title: "UX-UI Design",
    image: require("../assets/images/uiux-dev-vector.png"),
    shortDesc:
      "Designing modern, user-centric interfaces and user journeys that eliminate conversion friction.",
    technologies: ["Figma", "Adobe XD", "Sketch", "Canva"],
    detailsPageImages: {
      first: require("../assets/images/services/uiux1.jpg"),
      second: require("../assets/images/services/uiux2.png"),
      third: require("../assets/images/services/uiux3.png"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Designing User-Centric Digital Interfaces and Strategic UX Flows
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Design is not merely visual aesthetics; it is the logic of how a digital product operates, guides, and resolves user intent. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we combine human-centered design principles with conversion metrics. We create user interfaces (UI) and user experiences (UX) that look modern, feel intuitive, and minimize conversion friction.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We believe that high-quality design is invisible. It allows the user to complete their tasks, make purchases, or analyze data without confusion. We map out user journeys, build interactive wireframes, and design components in Figma from first principles to ensure your product is clean, accessible, and aligned with your brand.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Our design team works closely with our engineers. By building unified design systems in Figma with reusable UI tokens and layouts, we ensure a smooth translation from design prototype to clean, high-performance HTML/CSS code, preventing visual inconsistencies during development.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Strategic Design Philosophy & Capabilities:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">In-Depth User Research & Journey Mapping:</span> 
      Conduct user interviews, compile persona sheets, and map out complete user flows to locate and resolve friction points in your onboarding or checkout sequences.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Interactive Prototyping & Wireframing:</span> 
      Build structural wireframes and interactive prototypes in Figma, letting you experience the product flow and navigation hierarchy before starting the coding phase.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Unified Figma Design Systems:</span> 
      Construct modular, reusable component libraries with unified tokens (colors, typography, grid spacing) to ensure visual consistency across web, mobile, and print.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Web Accessibility (a11y) Audits & Compliance:</span> 
      Design digital interfaces in strict alignment with WCAG 2.1 AA guidelines, optimizing color contrast ratios, tap targets, and screen reader structures.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Why UX-UI Design Matters:
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    A well-engineered interface is one of the most effective ways to lower user acquisition costs and increase lifetime value. Clean UI and logical layout keep visitors on your site longer, reducing bounce rates and improving engagement signals.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    By resolving UI and layout problems during the design phase, we prevent expensive redesigns during development, saving engineering hours and speeding up launch times.
  </p>
  `,
    },
  },
  {
    title: "Blockchain",
    image: require("../assets/images/blockchain-dev-vector.png"),
    shortDesc:
      "Architecting secure decentralized trust layers, smart contracts, and Web3 application integrations.",
    technologies: ["JavaScript", "Python", "Rust", "Java", "Kotlin"],
    detailsPageImages: {
      first: require("../assets/images/services/blockchain1.jpg"),
      second: require("../assets/images/services/blockchain2.png"),
      third: require("../assets/images/services/blockchain3.png"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Decentralizing the Future with Secure Web3 Engineering
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Distributed ledger technology (blockchain) is transforming how modern enterprises secure data, manage transactions, and verify ownership. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we are Web3 engineers. We write secure smart contracts, build decentralized applications (dApps), and integrate private blockchain networks for logistics, finance, and security.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We write smart contracts in Solidity and Rust, deploying on Ethereum, Solana, Layer-2 networks, and EVM-compatible chains. We prioritize gas-fee optimization, smart contract security, and secure transaction endpoints, ensuring that all deployed code is audited and protected against vulnerabilities like re-entrancy, arithmetic overflow, and front-running.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Beyond public chains, we architect private, permissioned ledgers (Hyperledger Fabric) for enterprise supply chain visibility. By creating immutable record systems, we help logistics and finance companies track assets, verify origin points, and automate compliance workflows with complete transparency.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Decentralized Engineering Solutions:
  </h2>
  <ul class="list-disc pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Custom Smart Contract Auditing & Development:</span> 
      Write Solidity/Rust contracts, optimize gas fees, and run static analysis testing to ensure secure execution on-chain.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Decentralized App (dApp) Integrations:</span> 
      Develop web and mobile frontends that connect to on-chain contracts, integrating wallet providers like MetaMask, Phantom, and WalletConnect.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Private & Permissioned Enterprise Blockchains:</span> 
      Deploy secure, private ledgers (Hyperledger, Corda) to track supply chains, audit registers, and sync inter-company transactions securely.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Tokenomics & Asset Tokenization:</span> 
      Design and deploy ERC-20, ERC-721, and ERC-1155 token standards to facilitate digital ownership, fractional assets, and community governance.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Web3 Security Auditing & Compliance
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    The immutable nature of blockchain means that once a smart contract is deployed, it cannot be easily changed. Therefore, security auditing must be completed before launch.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We run automated analysis tools and complete line-by-line manual code reviews of all smart contracts, ensuring the integrity of your Web3 platform.
  </p>
  `,
    },
  },
  {
    title: "Infrastructure",
    image: require("../assets/images/infrastructure-dev-vector.webp"),
    shortDesc:
      "Designing resilient, auto-scaling cloud architectures and secure CI/CD pipelines.",
    technologies: ["AWS", "Azure", "Docker", "Kubernetes", "Terraform"],
    detailsPageImages: {
      first: require("../assets/images/services/infrastructure1.webp"),
      second: require("../assets/images/services/infrastructure2.webp"),
      third: require("../assets/images/services/infrastructure3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Engineering High-Availability Cloud Backbones for Enterprise Scaling
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Modern digital applications require cloud-native infrastructures that are robust, secure, and auto-scaling. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we design and manage cloud architectures (AWS, Google Cloud, Azure) that support high traffic volume, ensure data compliance, and minimize operational costs. We transform server environments into reliable, reproducible infrastructures.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We practice Infrastructure as Code (IaC), writing modular Terraform files to declare all VPC networking, security groups, database instances, and container clusters. This ensures that your environments are version-controlled, documented, and can be redeployed or recovered in minutes, eliminating manual configuration errors.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Our DevOps engineers build automated continuous integration and continuous delivery (CI/CD) pipelines. By configuring systems like GitHub Actions, GitLab CI, or Jenkins, we automate the testing, containerization (Docker), and zero-downtime deployment (Kubernetes) of your applications, accelerating launch cycles and maintaining code quality.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Infrastructure & DevOps capabilities:
  </h2>
  <ul class="list-disc pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Cloud-Native Architectures (AWS, Azure, GCP):</span> 
      Design elastic, multi-zone environments utilizing serverless, auto-scaling clusters, and CDN edge distributions to reduce global page load times.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Infrastructure as Code (IaC - Terraform):</span> 
      Write declarative, modular Terraform configurations to build, version, and manage cloud networks, virtual machines, and database instances.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Containerization & Orchestration (Docker/Kubernetes):</span> 
      Package applications into portable Docker containers and orchestrate them using Kubernetes clusters for efficient resource management and high availability.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">DevOps & Automated CI/CD Pipelines:</span> 
      Automate linting, unit testing, security vulnerability scans, and production deployments via GitHub Actions pipelines with zero-downtime rolling updates.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Security Auditing & Network Hardening:</span> 
      Configure strict IAM roles, VPC subnets, automated backups, and encrypted database connections to secure enterprise cloud data.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Cloud Cost Optimization & Compliance
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Many businesses overpay for cloud services due to idle resources and poor scaling rules. We analyze your cloud usage, optimizing server sizes, setting auto-scaling thresholds, and configuring cache layers to reduce cloud expenses by up to 50% without affecting performance.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We verify that your cloud setup meets localized compliance standards, aligning your database locations and backup policies with regulations in Europe, Australia, and the Middle East.
  </p>
  `,
    },
  },
  {
    title: "AI Automation",
    image: require("../assets/images/ai-automation.webp"),
    shortDesc:
      "Automating back-office workflows, database synchronizations, and API webhook pipelines.",
    technologies: ["Zapier", "Make", "Python", "UiPath", "Power Automate"],
    detailsPageImages: {
      first: require("../assets/images/services/aiautomation1.webp"),
      second: require("../assets/images/services/aiautomation2.webp"),
      third: require("../assets/images/services/aiautomation3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Operational Excellence & Back-Office Automation
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    In a competitive business environment, operational efficiency is critical for scaling. Manual data entry, disconnected software systems, and repetitive back-office processes slow down operations, increase costs, and lead to human errors. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we eliminate these bottlenecks by designing custom workflow automation systems.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We build custom middleware, integrate APIs from first principles, and utilize platforms like Make and Zapier to link your software tools. We create event-driven pipelines that automate lead scoring, invoice processing, CRM updates, and client onboarding, allowing information to sync instantly across your organization.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    By automating routine, repetitive tasks, we free your team to focus on strategic growth, creative problem-solving, and building client relationships. Our systems are built using secure, serverless Node.js and Python architectures, providing high reliability and scaling dynamically with your data volume.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Custom Automation Solutions:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">End-to-End Workflow Integration:</span> 
      Connect your CRM, sales funnels, financial registers, and marketing platforms, ensuring data updates automatically across all systems without manual entry.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Intelligent Document Processing (IDP):</span> 
      Transform unstructured data into actionable insights. Our AI-powered OCR systems automatically extract, validate, and enter data from invoices, contracts, forms, and receipts, eliminating manual data entry forever.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Bespoke Robotic Process Automation (RPA):</span> 
      Create custom browser automation scripts and RPA tools to sync data with legacy systems that do not offer public APIs.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Outbound Pipeline Autopilot:</span> 
      Automate lead scoring, rotators, multi-domain cold outreach campaigns, and CRM follow-up schedules to keep your sales pipelines consistently active.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Operational Accuracy & Business Scalability
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Replacing manual data transfers with automated pipelines eliminates the cost and risk of human input error. Our integrations ensure that lead registers, client profiles, and financial transaction lists are 100% accurate.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We build our automation systems with security in mind, encrypting data payloads in transit and enforcing strict API access controls to protect sensitive business records.
  </p>
  `,
    },
  },
  {
    title: "Data Analytics",
    image: require("../assets/images/data-analytics-vector.webp"),
    shortDesc:
      "Transforming raw business data into actionable dashboards and predictive machine learning models.",
    technologies: ["Python", "SQL", "Tableau", "PowerBI", "Pandas"],
    detailsPageImages: {
      first: require("../assets/images/services/dataanalytics1.webp"),
      second: require("../assets/images/services/dataanalytics2.webp"),
      third: require("../assets/images/services/dataanalytics3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Unlock Actionable Intelligence with Custom Data Engineering
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Data is a valuable asset, but its utility depends on how it is processed and analyzed. Modern businesses generate vast amounts of data daily, but often fail to translate it into clear, strategic decisions. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we design data pipelines and predictive modeling systems that help businesses make smart, data-driven decisions.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We set up automated ETL (Extract, Transform, Load) pipelines to pull data from your marketing, sales, and financial APIs into centralized data warehouses like BigQuery. This eliminates manual report compilation, providing your team with a single source of truth for business intelligence.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We design and build interactive visual dashboards and custom analytics panels using Tableau, PowerBI, and custom React interfaces. By displaying real-time operational and financial metrics clearly, we help you identify bottlenecks, monitor sales performance, and optimize business processes.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Data Analytics Capabilities:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Bespoke BI Visualization Dashboards:</span> 
      Consolidate disparate data streams into unified, real-time dashboards with interactive controls, charts, and drill-down metrics.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Automated ETL & Data Warehousing:</span> 
      Build secure data pipelines to extract, clean, transform, and load raw business data directly into centralized cloud warehouses (Snowflake, BigQuery).
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Predictive Modeling & Demand Forecasting:</span> 
      Train machine learning models to analyze historical trends, forecast inventory needs, detect database anomalies, and predict customer churn.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Funnel and Customer Journey Analytics:</span> 
      Track and map user actions across web and mobile platforms to optimize conversion rates, uncover usability bottlenecks, and boost retention.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Real-Time Business Insights & Data Security
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Accessing real-time operational metrics allows you to react to market shifts instantly, replacing delayed end-of-month reports with continuous business intelligence.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We implement strict database security and access control models, ensuring your business intelligence and customer records remain completely secure.
  </p>
  `,
    },
  },
  {
    title: "Startup MVP Development",
    image: require("../assets/images/web-dev-vector.png"),
    shortDesc:
      "Engineering market-ready Minimum Viable Products in 2 to 4 weeks for early-stage founders and venture-backed startups.",
    technologies: ["React", "Next.js", "Node.js", "Python", "Flutter", "Supabase", "OpenAI"],
    detailsPageImages: {
      first: require("../assets/images/services/webdevelopment1.jpg"),
      second: require("../assets/images/services/appdevelopment2.png"),
      third: require("../assets/images/services/aiautomation3.webp"),
    },
    html: {
      firstSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Rapid 2-to-4 Week MVP Engineering for High-Growth Startups
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    For early-stage founders, speed to market is the single most decisive factor in building a category-defining startup. Delaying your product launch by months means burning precious runaway, missing investor pitch windows, and losing early adopter momentum. At <span class="font-semibold text-primary">PANTHM AI Labs</span>, we provide dedicated **Startup MVP Development Services** designed to take your concept from wireframe to a fully functional, production-grade Minimum Viable Product in just 2 to 4 weeks.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Unlike traditional dev agencies that drag out scoping or outsource code to low-quality vendors, our core team of senior full-stack engineers and AI architects builds custom software from first principles. We combine modern Web & Mobile application stacks (React, Next.js, Flutter) with serverless cloud backends (Node.js, PostgreSQL/Supabase, Firebase) and native AI integrations (OpenAI LLMs, Voice SDRs) to deliver scalable software engineered with zero technical debt.
  </p>
  <p class="mb-10 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    Whether you are preparing for Y-Combinator, raising a Pre-Seed / Seed round, or validating product-market fit (PMF) with real paying customers, our MVP development sprints equip you with a secure, high-performance product built to impress founders, users, and venture capitalists alike.
  </p>
  `,
      secondSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Our Startup MVP Development Capabilities:
  </h2>
  <ul class="list-decimal pl-6 mb-8 text-slate-700 dark:text-slate-300 space-y-4">
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Rapid 2-to-4 Week Sprint Launch:</span> 
      Execute high-velocity development sprints focused on core value proposition features, ensuring your product is live and acquiring users fast.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">AI-Native & Intelligent Workflows:</span> 
      Integrate custom LLM pipelines, AI voice telecalling agents, vector database search, and automated workflows to give your MVP a unfair product advantage.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Cross-Platform Web & Mobile Engineering:</span> 
      Build responsive web applications and native iOS/Android mobile apps using React, Next.js, and Flutter with seamless state synchronization.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Zero Technical Debt & Seed-Scale Readiness:</span> 
      Architect modular codebases and serverless database schemas (PostgreSQL, Supabase, MongoDB) ready to scale to thousands of daily active users without refactoring.
    </li>
    <li class="mb-3">
      <span class="font-semibold text-slate-900 dark:text-white text-base">Stripe Payment & Auth Integration:</span> 
      Deploy secure user authentication (OAuth, Magic Links), subscription billing, and multi-tenant admin dashboards out of the box.
    </li>
  </ul>
  `,
      thirdSection: `<h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
    Why Founders Partner with PANTHM AI Labs for MVP Launches
  </h2>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    We act as your technical co-founder team. We don't just write code; we help founders scope lean feature sets, optimize UX conversion flows, and build robust database architectures that satisfy technical due diligence during investor fundraising.
  </p>
  <p class="mb-6 text-slate-700 dark:text-slate-300 leading-relaxed text-base">
    From initial product wireframing to live production deployment on Vercel and AWS, PANTHM provides end-to-end technical execution with clear milestone deliverables and full source code ownership transferred to your company.
  </p>
  `,
    },
  },
];
